import { __decorate } from "tslib";
import { localized, msg } from '@lit/localize';
import { html } from 'lit';
import { createRef, ref } from 'lit-html/directives/ref.js';
import { customElement, property, queryAll, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { SlotController } from '../../shared/controllers/slot.controller.js';
import { findForm } from '../../shared/forms/form-utils.js';
import { componentStyles } from '../../shared/styles/component.styles.js';
import { formControlStyles } from '../../shared/styles/form-control.styles.js';
import HelixElement from '../../shared/types/helix-element.js';
import '../icon/icon.js';
import { oneTimeCodeStyles } from './one-time-code.styles.js';
let HlxOneTimeCode = class HlxOneTimeCode extends HelixElement {
    constructor() {
        super(...arguments);
        this.label = '';
        this.name = '';
        this.value = '';
        this.placeholder = '';
        this.errorText = '';
        this.helpText = '';
        this.disabled = false;
        this.readonly = false;
        this.title = '';
        this.length = 6;
        this.validCharacterRegex = '^[0-9]$';
        this.mergedInputEl = createRef();
        this.slotController = new SlotController(this, 'label', 'error-text', 'help-text');
    }
    connectedCallback() {
        super.connectedCallback();
        this.setCharacters();
    }
    firstUpdated(changedProperties) {
        this.setInputRefs();
    }
    render() {
        const hasLabel = this.label || this.slotController.hasSlot('label');
        const hasErrorText = this.errorText || this.slotController.hasSlot('error-text');
        const hasHelpText = this.helpText || this.slotController.hasSlot('help-text');
        return html `
            <div
                part="hlx-form-control"
                class="${classMap({
            'hlx-form-control': true,
            'hlx-form-control-has-label': hasLabel,
            'hlx-form-control-has-error-text': hasErrorText,
            'hlx-form-control-has-help-text': hasHelpText,
        })}">
                <div
                    part="hlx-form-control-label"
                    class="${classMap({
            'hlx-form-control-label': true,
            'hlx-form-control-label-disabled': this.disabled,
        })}">
                    <slot name="label">${this.label}</slot>
                </div>

                <div
                    part="base"
                    class="${classMap({
            'hlx-one-time-code': true,
        })}">
                    ${this.characters.map((character, index) => html `
                            <input
                                part="input"
                                type="text"
                                class="${classMap({
            'hlx-one-time-code-control': true,
            'hlx-one-time-code-control-disabled': this.disabled,
            'hlx-one-time-code-control-readonly': this.readonly,
        })}"
                                name=${ifDefined(this.name)}
                                .value=${character.value}
                                placeholder=${ifDefined(this.placeholder)}
                                title=${this.title}
                                autocomplete=${'one-time-code'}
                                ?disabled=${this.disabled}
                                ?readonly=${this.readonly}
                                @change=${this.handleChange}
                                @blur=${this.handleBlur}
                                @focus=${this.handleFocus}
                                @input=${this.handleInput}
                                @invalid=${this.handleInvalid}
                                @keydown=${this.handleKeydown}
                                @paste=${this.handlePaste} />
                        `)}
                    <input
                        class="hlx-one-time-code-merged-input"
                        type="hidden"
                        .value=${this.value}
                        ${ref(this.mergedInputEl)} />
                </div>

                <div part="hlx-form-control-error-text" class="hlx-form-control-error-text">
                    <hlx-icon
                        class="hlx-form-control-error-icon"
                        name="circle-exclamation"
                        title="${msg('Error icon')}"></hlx-icon>
                    <slot name="error-text" id="error-text" role="alert" aria-live="assertive">
                        ${this.errorText}
                    </slot>
                </div>

                <div
                    part="hlx-form-control-help-text"
                    class="${classMap({
            'hlx-form-control-help-text': true,
            'hlx-form-control-help-text-disabled': this.disabled,
        })}">
                    <slot name="help-text">${this.helpText}</slot>
                </div>
            </div>
        `;
    }
    get validity() {
        return this.mergedInputEl.value.validity;
    }
    get validationMessage() {
        return this.mergedInputEl.value.validationMessage;
    }
    checkValidity() {
        return this.mergedInputEl.value.checkValidity();
    }
    reportValidity() {
        return this.mergedInputEl.value.reportValidity();
    }
    setCustomValidity(message) {
        this.errorText = message;
        this.mergedInputEl.value.setCustomValidity(message);
    }
    getForm() {
        return findForm(this);
    }
    handleChange() {
        this.value = this.mergedInputEl.value.value;
        this.emit('hlx-change');
    }
    handleBlur(event) {
        /** Emits hlx-blur if none of the child inputs are focused. */
        const anyInputsFocused = this.characters.some((c) => c.inputRef === event.relatedTarget);
        if (!anyInputsFocused) {
            console.log('hlx-blur');
            this.emit('hlx-blur');
        }
    }
    handleFocus(event) {
        /** Emits hlx-focus if none of the child inputs are already focused. */
        const anyInputsFocused = this.characters.some((c) => c.inputRef === event.relatedTarget);
        if (!anyInputsFocused) {
            console.log('hlx-focus');
            this.emit('hlx-focus');
        }
        const inputRef = event.target;
        event.preventDefault();
        inputRef.select();
    }
    handleInput(event) {
        // this.value = this.input.value;
        // this.formControlController.updateValidity();
        // this.emit('hlx-input');
        const inputRef = event.target;
        const index = this.getIndexByInputRef(inputRef);
        if (this.isValidCharacter(inputRef.value)) {
            this.shiftCaretRight(index);
        }
        else {
            inputRef.value = '';
        }
    }
    handleKeydown(event) {
        const inputRef = event.target;
        const index = this.getIndexByInputRef(inputRef);
        switch (event.key) {
            case 'ArrowLeft':
            case 'Left':
                event.preventDefault();
                this.select(index - 1);
                break;
            case 'ArrowRight':
            case 'Right':
                event.preventDefault();
                this.select(index + 1);
                break;
            case 'Backspace':
            case 'Delete':
            case 'Del':
                if (!inputRef.value) {
                    event.preventDefault();
                    this.select(index - 1);
                }
                break;
            case 'Home':
                event.preventDefault();
                this.select(0);
                break;
            case 'End':
                event.preventDefault();
                this.select(this.length - 1);
                break;
            default:
                inputRef.select();
                break;
        }
    }
    handleInvalid(event) {
        event.preventDefault();
        event.target.focus();
    }
    handlePaste(event) {
        event.preventDefault();
        let content = event.clipboardData.getData('text/plain');
        content = content.replace(/\s/g, ''); // Strip spaces
        content = content.substring(0, this.length); // Trim down to the allowed length
        // this.isValidCharacter()
        this.characters.forEach((c, i) => {
            const v = content[i] ? content[i] : '';
            c.value = v;
            c.inputRef.value = v;
        });
        this.value = content;
    }
    setCharacters() {
        this.characters =
            this.length <= 0 ?
                []
                : Array.from({ length: this.length }, ((value, index) => {
                    return {
                        value: (this.value && this.value[index]) || '',
                        inputRef: null,
                    };
                }).bind(this));
    }
    setInputRefs() {
        this.characters.forEach((c, i) => (c.inputRef = this.inputRefs[i]));
    }
    getIndexByInputRef(inputRef) {
        return this.characters.findIndex((c) => {
            return c.inputRef === inputRef;
        });
    }
    focusCharacter(index) {
        if (index > -1 && index < this.length) {
            this.characters[index]?.inputRef?.focus();
        }
    }
    select(index) {
        if (index > -1 && index < this.length) {
            this.characters[index]?.inputRef?.select();
        }
    }
    shiftCaretRight(index) {
        if (index + 1 === this.length) {
            return;
        }
        else if (this.characters[index + 1].value) {
            this.select(index + 1);
        }
        else {
            this.focusCharacter(index + 1);
        }
    }
    isValidCharacter(char) {
        return new RegExp(this.validCharacterRegex).test(char);
    }
};
HlxOneTimeCode.styles = [componentStyles, formControlStyles, oneTimeCodeStyles];
__decorate([
    property({ type: String })
], HlxOneTimeCode.prototype, "label", void 0);
__decorate([
    property({ type: String })
], HlxOneTimeCode.prototype, "name", void 0);
__decorate([
    property({ type: String })
], HlxOneTimeCode.prototype, "value", void 0);
__decorate([
    property({ type: String })
], HlxOneTimeCode.prototype, "placeholder", void 0);
__decorate([
    property({ type: String, attribute: 'error-text' })
], HlxOneTimeCode.prototype, "errorText", void 0);
__decorate([
    property({ type: String, attribute: 'help-text' })
], HlxOneTimeCode.prototype, "helpText", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], HlxOneTimeCode.prototype, "disabled", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], HlxOneTimeCode.prototype, "readonly", void 0);
__decorate([
    property({ type: String })
], HlxOneTimeCode.prototype, "title", void 0);
__decorate([
    property({ type: Number, reflect: true })
], HlxOneTimeCode.prototype, "length", void 0);
__decorate([
    property({ type: String, attribute: 'valid-character-regex' })
], HlxOneTimeCode.prototype, "validCharacterRegex", void 0);
__decorate([
    state()
], HlxOneTimeCode.prototype, "characters", void 0);
__decorate([
    queryAll('.hlx-one-time-code-control')
], HlxOneTimeCode.prototype, "inputRefs", void 0);
HlxOneTimeCode = __decorate([
    customElement('hlx-one-time-code'),
    localized()
    // TODO implementation of these interfaces is not actually correct, e.g. validation controller
    // is not used
], HlxOneTimeCode);
export { HlxOneTimeCode };
//# sourceMappingURL=one-time-code.js.map